import { Fragment, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { ClientScore, ScoringCategory, ScoringFunction, SupplierRating } from '../../../../types/advise';
import { AdjustmentNoteDialog } from './AdjustmentNoteDialog';
import { FunctionRatingRow } from './FunctionRatingRow';
import { FunctionScoreDetails } from './FunctionScoreDetails';
import { ScoringCategoryRow } from './ScoringCategoryRow';
import { categoryClientScore, categoryScore, ratingKey, ScoringView } from './SupplierScoring.calc';

// The clients' scores beside UPSTACK's own.
export interface TableClientScoring {
  view: ScoringView;
  clientScores: Map<string, ClientScore>;
  ratingRows: Map<string, SupplierRating>;
  openDetails: Set<string>;
  onToggleDetails: (functionId: string) => void;
}

interface FunctionScoringTableProps {
  categories: ScoringCategory[];
  supplierId: string;
  ratings: Map<string, number>;
  // Left out for a reader, who can't change a rating. A rating off the client average carries its note.
  onRate?: (functionId: string, rating: number, note?: string | null) => void;
  disabled?: boolean;
  // On a phone each function's name takes a line, with its slider and score beneath it.
  stackOnPhone?: boolean;
  client?: TableClientScoring;
}

interface NoteRequest {
  fn: ScoringFunction;
  rating: number;
  clientAverage: number;
  note: string;
}

const STACKED_ON_PHONE = (theme: Theme) => ({
  [theme.breakpoints.down('sm')]: {
    '& thead, & colgroup': { display: 'none' },
    '& table, & tbody': { display: 'block' },
    '& tr': { display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', columnGap: 2 },
    '& td': { display: 'block', borderBottom: 'none' },
    '& .function-name': { gridColumn: '1 / -1', pb: 0 }
  }
});

/** The active supplier's rating of every function, grouped by category. */
export const FunctionScoringTable = ({
  categories,
  supplierId,
  ratings,
  onRate,
  disabled = false,
  stackOnPhone = false,
  client
}: FunctionScoringTableProps) => {
  const [asking, setAsking] = useState<NoteRequest | null>(null);
  // Bumped when a note is cancelled, so the slider that asked for it drops back to the saved rating.
  const [resetToken, setResetToken] = useState(0);
  const showingClients = client?.view === 'client';

  const clientScoreOf = (fn: ScoringFunction) => client?.clientScores.get(ratingKey(supplierId, fn.id));

  const askForNote = (fn: ScoringFunction, rating: number, clientScore: ClientScore) =>
    setAsking({
      fn,
      rating,
      clientAverage: clientScore.average,
      note: client?.ratingRows.get(ratingKey(supplierId, fn.id))?.note ?? ''
    });

  // A release on the client average, or on a function no client scored, saves at once; anywhere else asks why first.
  const release = (fn: ScoringFunction, rating: number) => {
    const clientScore = clientScoreOf(fn);
    if (!onRate) return;
    if (rating === ratings.get(ratingKey(supplierId, fn.id))) setResetToken((token) => token + 1);
    else if (!clientScore || rating === clientScore.average) onRate(fn.id, rating, null);
    else askForNote(fn, rating, clientScore);
  };

  const editNote = (fn: ScoringFunction, clientScore: ClientScore) => {
    const rating = ratings.get(ratingKey(supplierId, fn.id));
    if (rating !== undefined) askForNote(fn, rating, clientScore);
  };

  return (
    <>
      <TableContainer sx={stackOnPhone ? STACKED_ON_PHONE : undefined}>
        <Table
          size="small"
          sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: client ? '40%' : '45%' }} />
            <col />
            <col style={{ width: '15%' }} />
            {client && <col style={{ width: '12%' }} />}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>FUNCTION</TableCell>
              <TableCell>{showingClients ? 'CLIENT AVERAGE' : 'YOUR RATING (0–100)'}</TableCell>
              <TableCell align="center">SCORE</TableCell>
              {client && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => (
              <Fragment key={category.id}>
                <ScoringCategoryRow
                  category={category}
                  score={categoryScore(category, supplierId, ratings)}
                  clientScore={
                    showingClients ? categoryClientScore(category, supplierId, client.clientScores) : undefined
                  }
                  withDetails={!!client}
                />
                {category.functions.map((fn) => {
                  const key = ratingKey(supplierId, fn.id);
                  const rating = ratings.get(key);
                  const clientScore = clientScoreOf(fn);
                  const detailsOpen = !!client?.openDetails.has(fn.id);
                  const rate = onRate && ((value: number) => onRate(fn.id, value, null));
                  const onRelease = onRate && ((value: number) => release(fn, value));
                  return (
                    <Fragment key={fn.id}>
                      <FunctionRatingRow
                        fn={fn}
                        rating={rating}
                        onRate={rate}
                        onRelease={client ? onRelease : undefined}
                        disabled={disabled}
                        resetToken={resetToken}
                        client={
                          client && {
                            view: client.view,
                            clientScore,
                            detailsOpen,
                            onToggleDetails: () => client.onToggleDetails(fn.id)
                          }
                        }
                      />
                      {client && detailsOpen && (
                        <FunctionScoreDetails
                          fn={fn}
                          rating={rating}
                          ratingRow={client.ratingRows.get(key)}
                          clientScore={clientScore}
                          onRate={rate}
                          onRelease={onRelease}
                          onUseAverage={onRate && clientScore && (() => onRate(fn.id, clientScore.average, null))}
                          onEditNote={onRate && clientScore && (() => editNote(fn, clientScore))}
                          disabled={disabled}
                          resetToken={resetToken}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {asking && (
        <AdjustmentNoteDialog
          functionLabel={asking.fn.label}
          rating={asking.rating}
          clientAverage={asking.clientAverage}
          initialNote={asking.note}
          onSave={(note) => {
            onRate?.(asking.fn.id, asking.rating, note);
            setAsking(null);
          }}
          onCancel={() => {
            setAsking(null);
            setResetToken((token) => token + 1);
          }}
        />
      )}
    </>
  );
};
